/* IS line role taxonomy — Phase 2a of chart-of-accounts refactor.
   Consumed by buildISLines.js to assemble a per-company IS_LINES array
   from gl_line_mappings (role/subsection/sort_order) plus the synthetic
   lines defined here. */

const r2 = n => Math.round(n * 100) / 100;

export const LINE_ROLES = {
  // Leaf / mapped roles — seeded onto gl_line_mappings.role
  REVENUE_LINE: 'revenue_line',
  PAYROLL_WAGES: 'payroll_wages',
  PAYROLL_BONUSES: 'payroll_bonuses',
  PAYROLL_COMMISSIONS: 'payroll_commissions',
  PAYROLL_TAX: 'payroll_tax',
  PAYROLL_BENEFITS: 'payroll_benefits',
  VENDOR_LINE: 'vendor_line',
  DEPRECIATION: 'depreciation',
  AMORTIZATION: 'amortization',
  MANUAL_EXPENSE: 'manual_expense',

  // Section / total roles — on synthetic lines (may also appear on
  // computed GL aggregate rows in the future)
  REVENUE_TOTAL: 'revenue_total',
  COGS_TOTAL: 'cogs_total',
  SM_TOTAL: 'sm_total',
  GA_TOTAL: 'ga_total',
  RD_TOTAL: 'rd_total',
  SW_TOTAL: 'sw_total',
  PF_TOTAL: 'pf_total',
  TME_TOTAL: 'tme_total',
  OOE_TOTAL: 'ooe_total',
  SGA_TOTAL: 'sga_total',

  // Other income/expense roles
  OI_INTEREST: 'oi_interest',
  OI_TAXES: 'oi_taxes',
  OI_OTHER_EXP: 'oi_other_exp',
  OI_OTHER_INC: 'oi_other_inc',
  OIE_TOTAL: 'oie_total',

  // Final metric roles
  OPERATING_INCOME: 'operating_income',
  EBIT_MARGIN: 'ebit_margin',
  NET_INCOME: 'net_income',
  EBITDA: 'ebitda',
};

export const CATEGORY = {
  REVENUE: 'revenue',
  COGS: 'cogs',
  SM: 'sm',
  SW: 'sw',
  PF: 'pf',
  TME: 'tme',
  GA: 'ga',
  RD: 'rd',
  OOE: 'ooe',
  OIE: 'oie',
};

/* role → source mapping for leaf lines (source field preserved on IS_LINES
   output; engines still rely on it). */
export const ROLE_TO_SOURCE = {
  [LINE_ROLES.REVENUE_LINE]: 'revenue',
  [LINE_ROLES.PAYROLL_WAGES]: 'payroll',
  [LINE_ROLES.PAYROLL_BONUSES]: 'payroll',
  [LINE_ROLES.PAYROLL_COMMISSIONS]: 'payroll',
  [LINE_ROLES.PAYROLL_TAX]: 'payroll',
  [LINE_ROLES.PAYROLL_BENEFITS]: 'payroll',
  [LINE_ROLES.VENDOR_LINE]: 'vendor',
  [LINE_ROLES.DEPRECIATION]: 'da',
  [LINE_ROLES.AMORTIZATION]: 'da',
  [LINE_ROLES.OI_INTEREST]: 'hardcoded',
  [LINE_ROLES.OI_TAXES]: 'vendor',
  [LINE_ROLES.OI_OTHER_EXP]: 'vendor',
  [LINE_ROLES.OI_OTHER_INC]: 'vendor',
};

/* roles that render as `conditional: true` on the IS (hidden when all zero) */
export const CONDITIONAL_ROLES = new Set([
  LINE_ROLES.PAYROLL_BONUSES,
  LINE_ROLES.PAYROLL_COMMISSIONS,
]);

/* Display group labels — translate display_group slug to user-facing label.
   Slugs are seeded on department_mappings.display_group (Session 1) and
   gl_line_mappings.display_group (Session 2b). Used by buildISLines.js when
   emitting display_group sub-section headers + totals (multi-dg path).
   Auto-titlecase fallback handles unknown slugs (e.g. future tenant labels)
   without requiring code changes. */
export const DISPLAY_GROUP_LABELS = {
  client_success: 'Client Success',
  rev_ops:        'Revenue Operations',
  data_science:   'Data Science',
  sales_mktg:     'Sales & Marketing',
};

export function displayGroupLabel(slug) {
  if (slug == null) return '';
  if (DISPLAY_GROUP_LABELS[slug]) return DISPLAY_GROUP_LABELS[slug];
  return slug.split('_')
    .map(w => w ? w.charAt(0).toUpperCase() + w.slice(1) : w)
    .join(' ');
}

/* IS category display↔slug translation — `is_category` in department_mappings
   and gl_line_mappings is stored in display form ('COGS', 'S&M', 'G&A', 'R&D',
   'Other'); `subsection` in gl_line_mappings is stored in slug form ('cogs',
   'sm', 'ga', 'rd'). Session 3 will consume this to translate
   department_mappings.is_category to subsection slug when building
   dynPayrollLeaves. 'Other' maps to null because OOE/OIE anchor on subsection
   directly. */
export const IS_CATEGORY_DISPLAY_TO_SLUG = {
  'COGS':  CATEGORY.COGS,
  'S&M':   CATEGORY.SM,
  'G&A':   CATEGORY.GA,
  'R&D':   CATEGORY.RD,
  'Other': null,
};

export const IS_CATEGORY_SLUG_TO_DISPLAY = Object.fromEntries(
  Object.entries(IS_CATEGORY_DISPLAY_TO_SLUG)
    .filter(([, slug]) => slug != null)
    .map(([disp, slug]) => [slug, disp])
);

/* Synthetic IS lines — not driven by gl_line_mappings. */
export const SYNTHETIC_LINES = {
  /* Always present in every company's IS regardless of GL data or module config. */
  alwaysPresent: [
    // --- Revenue header + total ---
    { id: 'rev_header', label: 'Revenue', level: 0, type: 'header' },
    { id: 'total_revenue', label: 'Total Revenue', level: 0, type: 'total', role: LINE_ROLES.REVENUE_TOTAL, sumFromSubsection: CATEGORY.REVENUE },

    // --- COGS header + total ---
    { id: 'cogs_header', label: 'Cost of Goods Sold', level: 0, type: 'header' },
    { id: 'total_cogs', label: 'Total Cost of Goods Sold', level: 0, type: 'total', role: LINE_ROLES.COGS_TOTAL, sumFromSubsection: CATEGORY.COGS },

    // --- Gross profit block ---
    { id: 'gross_profit', label: 'Gross Profit', level: 0, type: 'calc',
      calc: (d, m) => r2((d.total_revenue?.[m] || 0) - (d.total_cogs?.[m] || 0)) },
    { id: 'gp_margin', label: 'GP Margin', level: 1, type: 'margin', numId: 'gross_profit', denId: 'total_revenue' },

    // --- SG&A container header ---
    { id: 'sga_header', label: 'SG&A Expense', level: 0, type: 'header' },

    // --- S&M subsection ---
    { id: 'sm_header', label: 'Sales & Marketing', level: 1, type: 'header' },
    { id: 'total_sm', label: 'Total Sales & Marketing', level: 1, type: 'total', role: LINE_ROLES.SM_TOTAL, sumFromSubsection: CATEGORY.SM },

    // --- Software & Web Services subsection ---
    { id: 'sw_header', label: 'Software & Web Services', level: 1, type: 'header' },
    { id: 'total_sw', label: 'Total Software & Web Services', level: 1, type: 'total', role: LINE_ROLES.SW_TOTAL, sumFromSubsection: CATEGORY.SW },

    // --- Professional Fees subsection ---
    { id: 'pf_header', label: 'Professional Fees', level: 1, type: 'header' },
    { id: 'total_pf', label: 'Total Professional Fees', level: 1, type: 'total', role: LINE_ROLES.PF_TOTAL, sumFromSubsection: CATEGORY.PF },

    // --- Travel/Meals/Entertainment subsection ---
    { id: 'tme_header', label: 'Travel, Meals, & Entertainment', level: 1, type: 'header' },
    { id: 'total_tme', label: 'Total Travel, Meals & Entertainment', level: 1, type: 'total', role: LINE_ROLES.TME_TOTAL, sumFromSubsection: CATEGORY.TME },

    // --- G&A main subsection ---
    { id: 'ga_header', label: 'G&A', level: 1, type: 'header' },
    { id: 'total_ga', label: 'Total G&A', level: 1, type: 'total', role: LINE_ROLES.GA_TOTAL, sumFromSubsection: CATEGORY.GA },

    // --- R&D subsection ---
    { id: 'rd_header', label: 'R&D', level: 1, type: 'header' },
    { id: 'total_rd', label: 'Total R&D', level: 1, type: 'total', role: LINE_ROLES.RD_TOTAL, sumFromSubsection: CATEGORY.RD },

    // --- OOE subsection ---
    { id: 'ooe_header', label: 'Other Operating Expenses', level: 1, type: 'header' },
    { id: 'total_ooe', label: 'Total Other Operating Expenses', level: 1, type: 'total', role: LINE_ROLES.OOE_TOTAL, sumFromSubsection: CATEGORY.OOE },

    // --- Total SG&A (sums subsection totals, not leaves) ---
    { id: 'total_sga', label: 'Total SG&A Expenses', level: 0, type: 'total', role: LINE_ROLES.SGA_TOTAL,
      sumOfTotals: ['total_sm', 'total_sw', 'total_pf', 'total_tme', 'total_ga', 'total_rd', 'total_ooe'] },

    // --- Operating income + margin ---
    { id: 'operating_income', label: 'Operating Income', level: 0, type: 'calc', role: LINE_ROLES.OPERATING_INCOME,
      calc: (d, m) => r2((d.gross_profit?.[m] || 0) - (d.total_sga?.[m] || 0)) },
    { id: 'ebit_margin', label: 'EBIT Margin', level: 1, type: 'margin', role: LINE_ROLES.EBIT_MARGIN,
      numId: 'operating_income', denId: 'total_revenue' },

    // --- OIE section ---
    { id: 'oie_header', label: 'Other Income/Expenses', level: 0, type: 'header' },
    // oi_interest is always present (sweep interest lands here for every company
    // regardless of GL data — no historical line is required)
    // W-047.3 (Brian 10-02): conditional -- the Income Statement hides the row when it is $0 in every month
    // (a tenant that keeps a separate line and earns no sweep interest). It stays in the skeleton and in
    // total_oie either way; only the display hides it.
    { id: 'oi_interest', label: 'Interest (Income) / Expense', level: 1, source: 'hardcoded',
      role: LINE_ROLES.OI_INTEREST, subsection: CATEGORY.OIE, sort_order: 10, conditional: true },
    { id: 'total_oie', label: 'Total Other Income/Expense', level: 0, type: 'total', role: LINE_ROLES.OIE_TOTAL, sumFromSubsection: CATEGORY.OIE },

    // --- Final metrics ---
    { id: 'net_income', label: 'Net Income', level: 0, type: 'calc', role: LINE_ROLES.NET_INCOME,
      calc: (d, m) => r2((d.operating_income?.[m] || 0) - (d.total_oie?.[m] || 0)) },
    { id: 'ebitda', label: 'EBITDA', level: 0, type: 'calc', role: LINE_ROLES.EBITDA,
      // W-075: the D&A added back is ctx.daIds = daRouting.daLeafIds(isLines), handed in by computeIS --
      // every IS leaf whose stored role is depreciation/amortization in 'ooe', plus both synthetics. It
      // replaced four literal keys (other_depreciation, other_amortization, oi_depreciation_expense,
      // oi_amortization_expense), which missed a D&A line named otherwise. The same set feeds the CFS
      // add-back (orchestrator buildCFSIsRef). A superset of the four, never narrower; forecast (synthetic or
      // routed leaf) and history are disjoint by month and each id is summed once, so nothing double-counts.
      calc: (d, m, ctx) => r2(ctx.daIds.reduce((s, id) => s + (d[id]?.[m] || 0), d.operating_income?.[m] || 0)) },
  ],

  /* Module-gated leaf lines — present only when the owning module is enabled
     for the company. `enabledCheck(ctx)` returns a boolean where ctx is a
     module-context object the caller passes into buildISLinesForCompany.

     Session 0 (2026-04-27) removed the revenue moduleGated entries
     (rev_ae_quota, rev_company_quota) — those are now data-driven from
     revenue_module_mappings inside buildISLinesForCompany. Remaining entries
     are non-revenue (D&A) and stay static. */
  moduleGated: [
    { id: 'other_depreciation', label: 'Depreciation', level: 2, source: 'da',
      role: LINE_ROLES.DEPRECIATION, subsection: CATEGORY.OOE, sort_order: 10,
      enabledCheck: (ctx) => !!ctx.hasFixedAssets },
    { id: 'other_amortization', label: 'Amortization', level: 2, source: 'da',
      role: LINE_ROLES.AMORTIZATION, subsection: CATEGORY.OOE, sort_order: 20,
      enabledCheck: (ctx) => !!ctx.hasIntangibleAssets },
  ],
};

/* ============================================================================
   THE VENDOR-BOOKABLE LINE RULE (W-002, 2026-09-24) -- the ONE owner.
   A vendor may be written only onto a line the vendor engine BOOKS. computeVendor keeps exactly the
   leaves whose source is 'vendor' or 'hardcoded'; every other leaf -- payroll, D&A, revenue,
   implementation -- is skipped, so a vendor pointed at one books $0, silently. The skip is
   DELIBERATE: the payroll/vendor merge in runFullModel ASSIGNS, so a vendor-owned payroll line would
   OVERWRITE the payroll engine (measured: $7,091,509.95 of cid=36 forecast payroll). So the fix is
   to REFUSE such a vendor where it is written, never to stop the skip.
   Readers: computeVendor (the engine) - ISLinePicker.isVendorAssignable (the Vendor Dashboard and
   Scenarios pickers) - the Step-6 wizard (the 6c line options, the 6c gate and the import-click
   guard). Across THE WALL, server.js mirrors ROLE_TO_SOURCE and SYNTHETIC_LEAF_SOURCE (the W-002
   build asserts the mirrors equal these) and applies the same vendorLineBlockReason.
   ============================================================================ */
export const VENDOR_BOOKABLE_SOURCES = ['vendor', 'hardcoded'];
export function isVendorBookableSource(source) { return VENDOR_BOOKABLE_SOURCES.includes(source); }
export function isVendorBookableRole(role) { return isVendorBookableSource(ROLE_TO_SOURCE[role]); }
/* The synthetic leaves carry their own source -- no gl_line_mappings row exists for them. */
export const SYNTHETIC_LEAF_SOURCE = Object.fromEntries(
  [...SYNTHETIC_LINES.alwaysPresent, ...SYNTHETIC_LINES.moduleGated].filter(l => l.source).map(l => [l.id, l.source]));
/* Why a vendor may NOT be written onto `key`, or null when it may.
   roleOfKey: the role of the key's gl_line_mappings row, or undefined when the key has no row.
   POSITIVE by construction: a key with no row that is not a bookable synthetic is refused -- on a
   wizard-built tenant the per-department payroll keys have NO gl_line_mappings row at all. */
export function vendorLineBlockReason(key, roleOfKey) {
  const source = roleOfKey !== undefined ? ROLE_TO_SOURCE[roleOfKey] : SYNTHETIC_LEAF_SOURCE[key];
  if (roleOfKey === undefined && source === undefined) return 'not_a_line';
  if (isVendorBookableSource(source)) return null;
  if (source === 'payroll') return 'payroll';
  if (source === 'da') return 'da';
  if (source === 'revenue') return 'revenue';
  return 'not_bookable';
}
export const VENDOR_LINE_BLOCK_MESSAGE = {
  payroll: 'that line is a payroll line \u2014 a vendor cannot be assigned to it',
  da: 'that line is a depreciation or amortisation line \u2014 a vendor cannot be assigned to it',
  revenue: 'that line is a revenue line \u2014 a vendor cannot be assigned to it',
  not_bookable: 'the vendor forecast does not book that line \u2014 a vendor cannot be assigned to it',
  not_a_line: 'that key names no vendor-bookable line in the chart of accounts \u2014 a vendor cannot be assigned to it',
};
